import React, { useState, useEffect, useCallback } from 'react';
import { uniformsApi, studentsApi } from '../services/api';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Loader2, Search, TrendingUp, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const UniformsPage = () => {
  const [orders, setOrders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Multi-line items
  const [orderItems, setOrderItems] = useState([
    { item_type: '', size: '', quantity: 1 }
  ]);

  // Invoice tagging
  const [uniformInvoices, setUniformInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, suggestionsRes] = await Promise.all([
        uniformsApi.getOrders(),
        uniformsApi.getReorderSuggestions().catch(() => ({ data: [] }))
      ]);
      setOrders(ordersRes.data);
      setSuggestions(suggestionsRes.data);
    } catch (err) {
      toast.error('Failed to load uniform data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const searchStudents = async (query) => {
    if (!query || query.length < 2) {
      setStudentResults([]);
      return;
    }
    setSearchingStudent(true);
    try {
      const response = await studentsApi.getAll({ search: query, limit: 10 });
      setStudentResults(response.data.students);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchingStudent(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setStudentResults([]);
    setStudentSearch('');
    // Fetch invoices with uniform fee head for this student
    fetchUniformInvoices(student.id);
  };

  const fetchUniformInvoices = async (studentId) => {
    setLoadingInvoices(true);
    setSelectedInvoiceId('');
    try {
      const res = await api.get(`/uniforms/student-invoices/${studentId}`);
      setUniformInvoices(res.data);
    } catch {
      setUniformInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const addOrderItem = () => {
    setOrderItems([...orderItems, { item_type: '', size: '', quantity: 1 }]);
  };

  const removeOrderItem = (index) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    }
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderItems];
    newItems[index][field] = field === 'quantity' ? parseInt(value) || 1 : value;
    setOrderItems(newItems);
  };

  const handleAddOrder = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    
    // Validate all items have required fields
    const validItems = orderItems.filter(item => item.item_type && item.size && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }
    
    setSubmitting(true);
    try {
      await uniformsApi.createOrder({
        student_id: selectedStudent.id,
        items: validItems,
        invoice_id: selectedInvoiceId || null
      });
      toast.success('Uniform order created successfully');
      setShowAddDialog(false);
      setOrderItems([{ item_type: '', size: '', quantity: 1 }]);
      setSelectedStudent(null);
      setSelectedInvoiceId('');
      setUniformInvoices([]);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await uniformsApi.updateOrderStatus(orderId, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const uniformItems = ['Shirt', 'Trousers', 'Skirt', 'Sweater', 'Blazer', 'PE Kit', 'Socks', 'Shoes', 'Tie', 'Belt'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '24', '26', '28', '30', '32', '34', '36', '38', '40', '42'];
  const statuses = ['pending', 'processing', 'ready', 'delivered'];

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-600',
      processing: 'bg-[#D99C3B]/10 text-[#D99C3B]',
      ready: 'bg-blue-100 text-blue-600',
      delivered: 'bg-[#5556A8]/10 text-[#5556A8]'
    };
    return <Badge className={colors[status] || colors.pending}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Uniforms</h1>
          <p className="text-[#5C635F] mt-1">Manage uniform orders with multiple items per student</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-uniform-order-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Uniform Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddOrder} className="space-y-4">
              {/* Student Search */}
              <div className="space-y-2">
                <Label>Student</Label>
                {selectedStudent ? (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg">
                    <div>
                      <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                      <p className="text-sm text-[#5C635F]">{selectedStudent.student_id} - {selectedStudent.current_class}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedStudent(null)}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                    <Input
                      placeholder="Search student..."
                      value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                      className="pl-9"
                      data-testid="uniform-student-search"
                    />
                    {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-[#5C635F]" />}
                    {studentResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-[#D8D9E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {studentResults.map((student) => (
                          <button key={student.id} type="button" className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex items-center justify-between" onClick={() => handleStudentSelect(student)}>
                            <span className="font-medium">{student.first_name} {student.last_name}</span>
                            <span className="text-sm text-[#5C635F]">{student.current_class}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="space-y-3">

                {/* Invoice Tagging */}
                {selectedStudent && (
                  <div className="space-y-2">
                    <Label>Tag to Invoice (Uniform fee head)</Label>
                    {loadingInvoices ? (
                      <div className="flex items-center gap-2 text-sm text-[#5C635F]"><Loader2 className="w-4 h-4 animate-spin" />Loading invoices...</div>
                    ) : uniformInvoices.length > 0 ? (
                      <Select value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
                        <SelectTrigger data-testid="invoice-tag-select"><SelectValue placeholder="Select invoice (optional)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No invoice</SelectItem>
                          {uniformInvoices.map(inv => (
                            <SelectItem key={inv.id} value={inv.id}>
                              {inv.invoice_number} - ${inv.amount?.toFixed(2)} ({inv.status})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-[#5C635F]">No invoices with uniform fee head found</p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Label>Order Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addOrderItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {orderItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-[#F5F5FA] rounded-lg">
                      <div className="col-span-5">
                        <Select value={item.item_type} onValueChange={(v) => updateOrderItem(index, 'item_type', v)}>
                          <SelectTrigger data-testid={`item-type-${index}`}>
                            <SelectValue placeholder="Item" />
                          </SelectTrigger>
                          <SelectContent>
                            {uniformItems.map((ui) => (
                              <SelectItem key={ui} value={ui}>{ui}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Select value={item.size} onValueChange={(v) => updateOrderItem(index, 'size', v)}>
                          <SelectTrigger data-testid={`item-size-${index}`}>
                            <SelectValue placeholder="Size" />
                          </SelectTrigger>
                          <SelectContent>
                            {sizes.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)}
                          min="1"
                          placeholder="Qty"
                          data-testid={`item-qty-${index}`}
                        />
                      </div>
                      <div className="col-span-1 flex items-center justify-center">
                        {orderItems.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-[#C85A40] h-8 w-8 p-0"
                            onClick={() => removeOrderItem(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="p-3 bg-[#3E3F96]/5 rounded-lg">
                <p className="text-sm text-[#5C635F]">
                  Total Items: <span className="font-medium text-[#1C1F1D]">
                    {orderItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}
                  </span>
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-uniform-order-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Order
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reorder Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-[#D8D9E8] border-l-4 border-l-[#D99C3B]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#D99C3B]" />
              Reorder Suggestions (based on orders)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {suggestions.slice(0, 6).map((item, idx) => (
                <div key={idx} className="p-3 bg-[#F5F5FA] rounded-lg text-center">
                  <p className="font-medium text-[#1C1F1D]">{item.item_type}</p>
                  <p className="text-sm text-[#5C635F]">Size: {item.size}</p>
                  <p className="text-lg font-bold text-[#3E3F96]">{item.suggested_quantity}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders table */}
      <Card className="border-[#D8D9E8]">
        <CardHeader>
          <CardTitle className="text-lg">Recent Orders</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <Package className="w-12 h-12 mb-4 opacity-50" />
              <p>No uniform orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA]">
                    <TableHead className="text-xs uppercase font-semibold">Order #</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Items</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Total Qty</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium text-[#3E3F96]">{order.order_number || order.id?.slice(-8)}</TableCell>
                      <TableCell className="text-[#5C635F]">{formatDate(order.created_at)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.student_name}</p>
                          <p className="text-xs text-[#5C635F]">{order.student_class}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {order.items ? order.items.slice(0, 3).map((item, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {item.item_type} ({item.size}) x{item.quantity}
                            </Badge>
                          )) : (
                            <Badge variant="outline" className="text-xs">
                              {order.item_type} ({order.size}) x{order.quantity}
                            </Badge>
                          )}
                          {order.items && order.items.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{order.items.length - 3} more</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="tabular-nums font-medium">{order.total_items || order.quantity}</TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>
                        <Select value={order.status} onValueChange={(v) => handleStatusChange(order.id, v)}>
                          <SelectTrigger className="w-28 h-8" data-testid={`status-select-${order.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UniformsPage;
